import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  createObj,
  DateTimeProvider,
  enumGetValues,
  Id,
  IdGenerator,
  PaginatedResult,
  requireParam,
  runIfDefined,
  throwAccessDenied,
  throwInvalidParameter,
  throwObjectAlreadyExists,
  throwObjectNotFound,
  trCore,
  validateOptionalPhone,
  validateOptionalString,
  validateRequiredId,
  validateRequiredString,
} from '../../../common'
import {
  AddCitizenOpts,
  Citizen,
  CitizenCategory,
  CitizenFindOptions,
  CitizenFullInfo,
  CitizenSex,
  CitizensRepository,
  CitizensService,
  UpdateCitizenOpts,
} from './citizens'
import { CreateEventMode, ModelEventCreator, ModelEvent, ModelEventsProcessor } from '../../events'
import { ROTransaction, runWithROTransaction, RWTransaction } from '../../../db'
import { Permissions } from '../../authorization'
import {
  AddCitizenDocument,
  CitizenDocument,
  CitizenDocumentsRepository,
  DocumentType,
  getCitizenDocumentInfo,
} from '../../citizenDocuments'
import { MunicipalEntitiesRepository } from '../../municipalEntity'
import { differenceBy, isEqual } from 'lodash'
import { hashSync } from 'bcryptjs'
import { User, UsersRepository } from '../../users'
import { getIdentityCardStatus, IdentityCardsRepository } from '../../identityCards'
import { FullTextSearchService } from '../../fullTextSearch'

enum EventType {
  CITIZEN_ADD = 'CITIZEN_ADD',
  CITIZEN_UPDATE = 'CITIZEN_UPDATE',
  CITIZEN_DELETE = 'CITIZEN_DELETE',
  CITIZEN_MERGE = 'CITIZEN_MERGE',
}

type AddCitizenPayload = {
  surname: string
  name: string
  patronymic?: string
  sex: CitizenSex
  birthdate: string
  phone?: string
  municipalEntityId: Id
  locality: string
  street: string
  house: string
  appartment?: string
  category: CitizenCategory
  documents: AddCitizenDocumentPayload[]
}

type UpdateCitizenPayload = {
  surname?: string
  name?: string
  patronymic?: string
  sex?: CitizenSex
  birthdate?: string
  phone?: string
  municipalEntityId?: Id
  locality?: string
  street?: string
  house?: string
  appartment?: string
  category?: CitizenCategory
  documents?: AddCitizenDocumentPayload[]
  guardianIds?: Id[]
  dependentIds?: Id[]
}

type MergeCitizenPayload = {
  createdAt: string
  deletedAt?: string
  appartment?: string
  birthdate: string
  category: CitizenCategory
  dependentIds?: Id[]
  documents: AddCitizenDocumentPayload[]
  guardianIds?: Id[]
  house: string
  locality: string
  municipalEntityId: Id
  name: string
  patronymic?: string
  phone?: string
  sex: CitizenSex
  street: string
  surname: string
}

type AddCitizenDocumentPayload = {
  type: DocumentType
  series?: string
  num?: string
  issue?: string
  date?: string
  name?: string
  info?: string
}

interface DBCitizensServiceOpts extends BaseDBServiceOpts<CoreDict> {
  citizensRepository: CitizensRepository
  citizenDocumentsRepository: CitizenDocumentsRepository
  municipalEntitiesRepository: MunicipalEntitiesRepository
  usersRepository: UsersRepository
  identityCardsRepository: IdentityCardsRepository
  dateTimeProvider: DateTimeProvider
}

export class DbCitizensService extends BaseDBService<DBCitizensServiceOpts, CoreDict> implements CitizensService {
  constructor(opts: DBCitizensServiceOpts) {
    super('DbCitizensService', opts)
    this.logger.debug({}, '<777d52f4> Initialized')
  }

  private validateUserID = (userID?: string) => validateRequiredId('<e1b5a777> User ID', userID)
  private validateSurname = (surname?: string) => validateRequiredString('<2ef1d09c> Surname', surname?.trim())
  private validateName = (name?: string) => validateRequiredString('<2ef1d19c> Name', name?.trim())
  private validatePatronymic = (patronymic?: string) =>
    validateOptionalString('<2ef1d29c> Patronymic', patronymic?.trim())
  private validatePhone = (phone?: string) => validateOptionalPhone('<2ef1d39c> Телефон', phone?.trim())
  private validateLocality = (locality?: string) => validateRequiredString('<2ef1d49c> Locality', locality?.trim())
  private validateStreet = (street?: string) => validateRequiredString('<2ef1d59c> Street', street?.trim())
  private validateHouse = (house?: string) => validateRequiredString('<2ef1d69c> House', house?.trim())
  private validateAppartment = (appartment?: string) =>
    validateOptionalString('<2ef1d79c> Appartment', appartment?.trim())
  private validateMunicipalEntityId = (municipalEntityId?: string) =>
    validateRequiredId('<9510ded9> Municipal Entity Id', municipalEntityId)
  private validateSeriesDoc = (type: DocumentType, series?: string) => {
    const fieldName = '<5bf52541> Серия паспорта'
    const result = validateOptionalString(fieldName, series, { pattern: /^[0-9]{4}$/ })
    return type === DocumentType.PASSPORT ? requireParam(fieldName, result) : result
  }
  private validateNumDoc = (type: DocumentType, num?: string) => {
    const fieldName = '<18b17d1d> Номер паспорта'
    const result = validateOptionalString(fieldName, num, { pattern: /^[0-9]{6}$/ })
    return type === DocumentType.PASSPORT ? requireParam(fieldName, result) : result
  }
  private validateIssueDoc = (type: DocumentType, issue?: string) => {
    const fieldName = '<17109319> Issue'
    const result = validateOptionalString(fieldName, issue)
    return type === DocumentType.PASSPORT ? requireParam(fieldName, result) : result
  }
  private validateDateDoc = (type: DocumentType, date?: string) => {
    const fieldName = '<ecfed900> Date'
    const result = validateOptionalString(fieldName, date)
    return type === DocumentType.PASSPORT ? requireParam(fieldName, result) : result
  }
  private validateNameDoc = (type: DocumentType, name?: string) => {
    const fieldName = '<86f7ebe7> Name'
    const result = validateOptionalString(fieldName, name)
    return type === DocumentType.OTHER ? requireParam(fieldName, result) : result
  }
  private validateInfoDoc = (type: DocumentType, info?: string) => {
    const fieldName = '<f172932d> Info'
    const result = validateOptionalString(fieldName, info)
    return type === DocumentType.OTHER ? requireParam(fieldName, result) : result
  }

  private validateAddDocs = (docs: AddCitizenDocument[]): AddCitizenDocument[] => {
    this.validateSizeOfDocs(docs)
    this.validateCountOfPassports(docs)

    return docs
  }

  private validateUpdateDocs = (docs?: AddCitizenDocument[]): AddCitizenDocument[] | undefined => {
    if (!docs) return undefined
    this.validateCountOfPassports(docs)

    return docs
  }

  private validateSizeOfDocs = (docs: AddCitizenDocument[]): void => {
    if (docs.length === 0) {
      throwInvalidParameter('<75ca7c10> Documents must not be empty')
    }
  }

  private validateCountOfPassports = (docs: AddCitizenDocument[]): void => {
    if (docs.filter(d => d.type === DocumentType.PASSPORT).length > 1) {
      throwInvalidParameter('<55aa7c07> Passport must be one')
    }
  }

  private validatedDoc = (doc: AddCitizenDocument) => {
    return {
      type: doc.type,
      series: this.validateSeriesDoc(doc.type, doc.series),
      num: this.validateNumDoc(doc.type, doc.num),
      issue: this.validateIssueDoc(doc.type, doc.issue),
      date: this.validateDateDoc(doc.type, doc.date?.toISOString()),
      name: this.validateNameDoc(doc.type, doc.name),
      info: this.validateInfoDoc(doc.type, doc.info),
    }
  }

  private async getCurrentUser(ctx: Context, tx: ROTransaction): Promise<User> {
    const userID = this.validateUserID(ctx.userId)
    return (
      (await this.opts.usersRepository.findById(tx, userID)) ??
      throwObjectNotFound(`<b6b416d4> ${this.translate(ctx, d => d.users.notFoundByIdError, userID)}`)
    )
  }

  private async checkAccessToMunicipalEntity(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meId: string | undefined,
  ): Promise<void> {
    if (!meId) {
      throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.citizens.municipalEntityNotGiven)}`)
    }

    const currentUser = await this.getCurrentUser(ctx, tx)
    if (currentUser.municipalEntityId != meId) {
      throwAccessDenied(
        `${errorCode} ${this.opts.translationService.translate(
          ctx.langCode,
          d => d.citizens.municipalEntityAccessDenied,
        )}`,
      )
    }
  }

  private async checkReadAccessToCitizen(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meId: string | undefined,
  ): Promise<void> {
    if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CITIZENS_VIEW))) {
      await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CITIZENS_VIEW)
      await this.checkAccessToMunicipalEntity(errorCode, ctx, tx, meId)
    }
  }

  private async checkReadAccessToCitizenList(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    opts?: CitizenFindOptions,
  ): Promise<CitizenFindOptions | undefined> {
    if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CITIZENS_VIEW))) {
      await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CITIZENS_VIEW)
      if (!opts?.filter?.municipalEntityId) {
        const currentUser = await this.getCurrentUser(ctx, tx)
        opts = { ...opts, filter: { ...opts?.filter, municipalEntityId: currentUser.municipalEntityId } }
      }
      await this.checkAccessToMunicipalEntity(errorCode, ctx, tx, opts?.filter?.municipalEntityId)
    }
    return opts
  }

  private async checkWriteAccessToCitizen(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meId: string | undefined,
  ): Promise<void> {
    if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CITIZENS_MANAGE))) {
      await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CITIZENS_MANAGE)
      await this.checkAccessToMunicipalEntity(errorCode, ctx, tx, meId)
    }
  }

  private async getCitizen(ctx: Context, tx: ROTransaction, id: Id): Promise<Citizen> {
    return (
      (await this.opts.citizensRepository.findByIdNonDeleted(tx, id)) ??
      throwObjectNotFound(`<0bc0b6d8> ${this.translate(ctx, d => d.citizens.notFoundByIdError, id)}`)
    )
  }

  private async checkForMunicipalEntityExists(ctx: Context, tx: ROTransaction, id: Id) {
    ;(await this.opts.municipalEntitiesRepository.findByIdNonDeleted(tx, id)) ??
      throwObjectNotFound(`<7fea83c8> ${this.translate(ctx, d => d.citizens.municipalEntityNotFoundError, id)}`)
  }

  findAll(ctx: Context, opts?: CitizenFindOptions): Promise<PaginatedResult<Citizen>> {
    return runWithROTransaction(this.opts.dbConnection, async tx => {
      opts = await this.checkReadAccessToCitizenList('<3964f001>', ctx, tx, opts)
      return await this.opts.citizensRepository.findAll(tx, opts)
    })
  }

  async add(ctx: Context, opts: AddCitizenOpts): Promise<Id> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      await this.checkForMunicipalEntityExists(ctx, tx, opts.municipalEntityId)

      await this.checkWriteAccessToCitizen('<aa1ed5d0>', ctx, tx, opts.municipalEntityId)

      await this.checkCitizenUniqueness(ctx, tx, opts.documents)

      const municipalEntityId = this.validateMunicipalEntityId(opts.municipalEntityId)
      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId: this.opts.idGenerator.generate(),
        type: EventType.CITIZEN_ADD,
        municipalEntityId,
        payload: createObj<AddCitizenPayload>({
          surname: this.validateSurname(opts.surname.trim()),
          name: this.validateName(opts.name.trim()),
          patronymic: this.validatePatronymic(opts?.patronymic?.trim()),
          sex: opts.sex,
          birthdate: opts.birthdate.toISOString(),
          phone: this.validatePhone(opts?.phone?.trim()),
          municipalEntityId,
          locality: this.validateLocality(opts.locality.trim()),
          street: this.validateStreet(opts.street.trim()),
          house: this.validateHouse(opts.house.trim()),
          appartment: this.validateAppartment(opts?.appartment?.trim()),
          category: opts.category,
          documents: this.validateAddDocs(opts.documents).map(doc => this.validatedDoc(doc)),
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  findById(ctx: Context, id: Id): Promise<Citizen | undefined> {
    return runWithROTransaction(this.opts.dbConnection, async tx => {
      const citizen = await this.opts.citizensRepository.findById(tx, id)
      await this.checkReadAccessToCitizen('<620c230a>', ctx, tx, citizen?.municipalEntityId)
      return citizen
    })
  }

  async update(ctx: Context, id: Id, opts: UpdateCitizenOpts): Promise<void> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const citizen = await this.getCitizen(ctx, tx, id)
      await this.checkWriteAccessToCitizen('<e8821c9a>', ctx, tx, citizen?.municipalEntityId)

      if (opts.municipalEntityId) await this.checkForMunicipalEntityExists(ctx, tx, opts.municipalEntityId)
      if (opts.documents) await this.checkCitizenUniqueness(ctx, tx, opts.documents, citizen.id)

      const municipalEntityId = runIfDefined(opts.municipalEntityId, it => this.validateMunicipalEntityId(it))
      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.CITIZEN_UPDATE,
        municipalEntityId,
        payload: createObj<UpdateCitizenPayload>({
          surname: opts.surname ? this.validateSurname(opts.surname.trim()) : undefined,
          name: opts.name ? this.validateName(opts.name.trim()) : undefined,
          patronymic: opts.patronymic && this.validatePatronymic(opts.patronymic.trim()),
          sex: opts.sex,
          birthdate: opts.birthdate?.toISOString(),
          phone: opts.phone && this.validatePhone(opts.phone.trim()),
          municipalEntityId: opts.municipalEntityId ? municipalEntityId : undefined,
          locality: opts.locality ? this.validateLocality(opts.locality.trim()) : undefined,
          street: opts.street ? this.validateStreet(opts.street.trim()) : undefined,
          house: opts.house ? this.validateHouse(opts.house.trim()) : undefined,
          appartment: opts.appartment && this.validateAppartment(opts.appartment.trim()),
          category: opts.category,
          documents: this.validateUpdateDocs(opts.documents)?.map(doc => this.validatedDoc(doc)),
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async delete(ctx: Context, id: Id): Promise<void> {
    const toRemoveDependent: { dependentId: Id; guardianId: Id }[] = []

    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const citizen = await this.getCitizen(ctx, tx, id)
      await this.checkWriteAccessToCitizen('<606d85a1>', ctx, tx, citizen?.municipalEntityId)

      if (citizen.guardianIds && citizen.guardianIds.length > 0) {
        for (const guardianId of citizen.guardianIds) {
          toRemoveDependent.push({ dependentId: citizen.id, guardianId })
        }
      }

      if (citizen.dependentIds && citizen.dependentIds.length > 0) {
        for (const dependentId of citizen.dependentIds) {
          toRemoveDependent.push({ dependentId, guardianId: citizen.id })
        }
      }

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.CITIZEN_DELETE,
      })
    })

    for (const it of toRemoveDependent) {
      await this.removeDependent(ctx, it.dependentId, it.guardianId)
    }

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  findCitizenDocuments(ctx: Context, citizenId: Id): Promise<CitizenDocument[] | []> {
    return runWithROTransaction(this.opts.dbConnection, async tx => {
      const citizen = await this.opts.citizensRepository.findById(tx, citizenId)
      await this.checkReadAccessToCitizen('<f225cefe>', ctx, tx, citizen?.municipalEntityId)
      return await this.opts.citizenDocumentsRepository.findAllByCitizenIds(tx, [citizenId])
    })
  }

  async findAllWithInfo(ctx: Context, opts?: CitizenFindOptions): Promise<PaginatedResult<CitizenFullInfo>> {
    return runWithROTransaction(this.opts.dbConnection, async tx => {
      opts = await this.checkReadAccessToCitizenList('<2e2eef88>', ctx, tx, opts)

      const paginatedCitizens = await this.opts.citizensRepository.findAll(tx, opts)
      const documentsByCitizens = await this.opts.citizenDocumentsRepository.findAllByCitizenIds(
        tx,
        paginatedCitizens.items.map(citizen => citizen.id),
      )

      const municipalEntitiesByCitizens = await this.opts.municipalEntitiesRepository.findAll(tx, {
        filter: {
          ids: paginatedCitizens.items.map(citizen => citizen.municipalEntityId),
        },
      })

      const now = this.opts.dateTimeProvider.get()
      const m = opts?.filter?.month || now.getMonth()
      const y = opts?.filter?.year || now.getFullYear()
      const cardsByCitizens = await this.opts.identityCardsRepository.findAll(tx, {
        filter: {
          citizenIds: paginatedCitizens.items.map(citizen => citizen.id),
          month: opts?.filter?.month || m,
          year: opts?.filter?.year || y,
        },
      })
      const items = paginatedCitizens.items.map(citizen => {
        let citizenCard = cardsByCitizens.filter(card => card.citizenId === citizen.id)[0]
        if (!citizenCard && citizen.createdAt.getTime() < new Date(y, m + 1, 0).getTime()) {
          citizenCard = {
            id: '',
            month: m,
            year: y,
            citizenId: citizen.id,
            createdAt: now,
          }
        }
        return {
          citizen: citizen,
          document: getCitizenDocumentInfo(documentsByCitizens.filter(documents => documents.citizenId === citizen.id)),
          municipalEntity:
            municipalEntitiesByCitizens.items.find(me => me.id === citizen.municipalEntityId)?.name || '-',
          cardStatus: citizenCard && getIdentityCardStatus(citizenCard),
        }
      })
      return {
        total: paginatedCitizens.total,
        from: paginatedCitizens.from,
        count: paginatedCitizens.count,
        items: items,
      }
    })
  }

  async addDependent(ctx: Context, dependentId: Id, guardianId: Id): Promise<void> {
    if (dependentId === guardianId) {
      throwObjectNotFound(`<af8d902b> ${this.translate(ctx, d => d.citizens.cantBeTheSame)}`)
    }

    const events = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const guardian = await this.getCitizen(ctx, tx, guardianId)
      await this.checkWriteAccessToCitizen('<1c335433>', ctx, tx, guardian?.municipalEntityId)
      if (guardian.dependentIds?.includes(dependentId)) {
        throwInvalidParameter(`<660560f8> ${this.translate(ctx, d => d.citizens.guardianAlreadyHasDependent)}`)
      }
      const dependent = await this.getCitizen(ctx, tx, dependentId)
      await this.checkWriteAccessToCitizen('<34960dfc>', ctx, tx, dependent?.municipalEntityId)
      if (dependent.guardianIds?.includes(guardianId)) {
        throwInvalidParameter(`<8c3a9d48> ${this.translate(ctx, d => d.citizens.dependentAlreadyHasGuardian)}`)
      }

      if (guardian.dependentIds) {
        guardian.dependentIds.push(dependentId)
      }
      const guardianEvent = await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: guardianId,
        type: EventType.CITIZEN_UPDATE,
        payload: createObj<UpdateCitizenPayload>({
          dependentIds: guardian.dependentIds ? guardian.dependentIds : [dependentId],
        }),
      })

      if (dependent.guardianIds) {
        dependent.guardianIds.push(guardianId)
      }
      const dependentEvent = await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: dependentId,
        type: EventType.CITIZEN_UPDATE,
        payload: createObj<UpdateCitizenPayload>({
          guardianIds: dependent.guardianIds ? dependent.guardianIds : [guardianId],
        }),
      })
      return [guardianEvent, dependentEvent]
    })

    await this.opts.eventDispatcher.processEvents(ctx, events)
  }

  async removeDependent(ctx: Context, dependentId: Id, guardianId: Id): Promise<void> {
    const events = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const guardian = await this.getCitizen(ctx, tx, guardianId)
      await this.checkWriteAccessToCitizen('<3415b40a>', ctx, tx, guardian?.municipalEntityId)
      if (!guardian.dependentIds?.includes(dependentId)) {
        throwObjectNotFound(`<d31fce2b> ${this.translate(ctx, d => d.citizens.guardianDoesntRelatedWithDependent)}`)
      }

      const dependent = await this.getCitizen(ctx, tx, dependentId)
      await this.checkWriteAccessToCitizen('<fe9bbbeb>', ctx, tx, dependent?.municipalEntityId)
      if (!dependent.guardianIds?.includes(guardianId)) {
        throwObjectNotFound(`<8836ebbe> ${this.translate(ctx, d => d.citizens.dependentDoesntRelatedWithGuardian)}`)
      }

      if (guardian.dependentIds) {
        guardian.dependentIds = guardian.dependentIds.filter(dId => dId !== dependentId)
      }
      const guardianEvent = await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: guardianId,
        type: EventType.CITIZEN_UPDATE,
        payload: createObj<UpdateCitizenPayload>({
          dependentIds: guardian.dependentIds ? guardian.dependentIds : [],
        }),
      })

      if (dependent.guardianIds) {
        dependent.guardianIds = dependent.guardianIds.filter(gId => gId !== guardianId)
      }
      const dependentEvent = await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: dependentId,
        type: EventType.CITIZEN_UPDATE,
        payload: createObj<UpdateCitizenPayload>({
          guardianIds: dependent.guardianIds ? dependent.guardianIds : [],
        }),
      })

      return [guardianEvent, dependentEvent]
    })

    await this.opts.eventDispatcher.processEvents(ctx, events)
  }

  async checkCitizenUniqueness(
    ctx: Context,
    tx: ROTransaction,
    documentsOpts: AddCitizenDocument[],
    citizenID?: Id,
  ): Promise<void> {
    const passport = documentsOpts.find(doc => doc.type === DocumentType.PASSPORT)

    if (!passport || !passport.series || !passport.num || !passport.date) return

    const passportFromDB = await this.opts.citizenDocumentsRepository.findByPassportData(
      tx,
      passport.series,
      passport.num,
    )

    if (
      passportFromDB &&
      passportFromDB.date?.getFullYear() === passport.date?.getFullYear() &&
      passportFromDB.date?.getMonth() === passport.date?.getMonth() &&
      passportFromDB.date.getDate() === passport.date.getDate()
    ) {
      if (citizenID && passportFromDB.citizenId === citizenID) return

      throwObjectAlreadyExists(this.translate(ctx, d => d.citizens.alreadyExists))
    }
  }
}

function getFieldsToIndex(c: Citizen, documents: CitizenDocument[]): (string | undefined)[] {
  return [c.name, c.surname, c.patronymic, c.phone, c.locality, c.street].concat(
    documents.flatMap(d => [d.series, d.num, d.name, d.info]),
  )
}

export class CitizensModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: {
      citizensRepository: CitizensRepository
      citizenDocumentsRepository: CitizenDocumentsRepository
      idGenerator: IdGenerator
      eventCreator: ModelEventCreator
      fullTextSearchService: FullTextSearchService
    },
  ) {}

  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const c =
      (await this.opts.citizensRepository.findById(tx, entityId)) ??
      throwObjectNotFound(
        trCore(d => d.citizens.notFoundByIdError, entityId),
        { uid: '<270b0667>' },
      )
    const docs = await this.opts.citizenDocumentsRepository.findAllByCitizenIds(tx, [c.id])
    return await this.opts.eventCreator.create(ctx, tx, {
      entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.CITIZEN_MERGE,
      municipalEntityId: c.municipalEntityId,
      payload: createObj<MergeCitizenPayload>({
        ...c,
        createdAt: c.createdAt.toISOString(),
        deletedAt: c.deletedAt?.toISOString(),
        birthdate: c.birthdate.toISOString(),
        documents: docs.map(d => ({
          ...d,
          date: d.date?.toISOString(),
        })),
      }),
    })
  }

  private hashOfDocument(obj: CitizenDocument | AddCitizenDocumentPayload): string {
    return hashSync(`${obj.type}${obj.series}${obj.num}${obj.issue}${obj.date}${obj.name}${obj.info}`)
  }

  private async syncDocuments(
    tx: RWTransaction,
    opts: { documents: AddCitizenDocumentPayload[]; entityId: Id; createdAt: Date; isNew: boolean },
  ): Promise<CitizenDocument[]> {
    const existingDocuments = opts.isNew
      ? []
      : await this.opts.citizenDocumentsRepository.findAllByCitizenIds(tx, [opts.entityId])
    const documentsForDelete = differenceBy(existingDocuments, opts.documents, this.hashOfDocument)
    const documentsForAdd = differenceBy(opts.documents, existingDocuments, this.hashOfDocument)

    for (const document of documentsForDelete) {
      await this.opts.citizenDocumentsRepository.delete(tx, document.id, opts.createdAt)
    }

    const docs = documentsForAdd.map(d =>
      createObj<CitizenDocument>({
        ...d,
        id: this.opts.idGenerator.generate(),
        createdAt: opts.createdAt,
        date: runIfDefined(d.date, v => new Date(v)),
        citizenId: opts.entityId,
      }),
    )

    await this.opts.citizenDocumentsRepository.save(tx, docs)
    return docs
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.CITIZEN_ADD: {
        const p = event.payload as AddCitizenPayload
        const citizen: Citizen = {
          id: event.entityId,
          createdAt: event.createdAt,
          surname: p.surname,
          name: p.name,
          patronymic: p.patronymic,
          sex: p.sex,
          birthdate: new Date(p.birthdate),
          phone: p.phone,
          municipalEntityId: p.municipalEntityId,
          locality: p.locality,
          street: p.street,
          house: p.house,
          appartment: p.appartment,
          category: p.category,
        }
        await this.opts.citizensRepository.save(tx, citizen)

        const documents = await this.syncDocuments(tx, {
          documents: p.documents,
          createdAt: event.createdAt,
          entityId: event.entityId,
          isNew: true,
        })

        await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, getFieldsToIndex(citizen, documents))
        return true
      }
      case EventType.CITIZEN_UPDATE: {
        const p = event.payload as UpdateCitizenPayload
        const citizen =
          (await this.opts.citizensRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(`<8c69d844> Citizen not found by id=${event.entityId}`)

        const updatedCitizen: Citizen = {
          ...citizen,
          surname: p.surname ?? citizen.surname,
          name: p.name ?? citizen.name,
          patronymic: p.patronymic ?? citizen.patronymic,
          sex: p.sex ?? citizen.sex,
          birthdate: new Date(p.birthdate ?? citizen.birthdate),
          phone: p.phone ?? citizen.phone,
          municipalEntityId: p.municipalEntityId ?? citizen.municipalEntityId,
          locality: p.locality ?? citizen.locality,
          street: p.street ?? citizen.street,
          house: p.house ?? citizen.house,
          appartment: p.appartment ?? citizen.appartment,
          category: p.category ?? citizen.category,
          dependentIds: p.dependentIds ?? citizen.dependentIds,
          guardianIds: p.guardianIds ?? citizen.guardianIds,
        }
        await this.opts.citizensRepository.save(tx, updatedCitizen)

        const documents = await this.opts.citizenDocumentsRepository.findAllByCitizenIds(tx, [event.entityId])
        let updatedDocuments = documents
        if (p.documents && p.documents.length > 0) {
          await this.syncDocuments(tx, {
            documents: p.documents,
            createdAt: event.createdAt,
            entityId: event.entityId,
            isNew: false,
          })
          updatedDocuments = await this.opts.citizenDocumentsRepository.findAllByCitizenIds(tx, [event.entityId])
        }

        const fieldsToIndex = getFieldsToIndex(updatedCitizen, updatedDocuments)

        if (!isEqual(fieldsToIndex, getFieldsToIndex(citizen, documents))) {
          await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, fieldsToIndex)
        }

        return true
      }
      case EventType.CITIZEN_MERGE: {
        const p = event.payload as MergeCitizenPayload
        await this.opts.citizensRepository.save(tx, {
          ...p,
          id: event.entityId,
          birthdate: new Date(p.birthdate),
          createdAt: new Date(p.createdAt),
          deletedAt: runIfDefined(p.deletedAt, v => new Date(v)),
        })
        await this.syncDocuments(tx, {
          documents: p.documents,
          createdAt: event.createdAt,
          entityId: event.entityId,
          isNew: false,
        })
        await this.opts.fullTextSearchService.removeFromIndex(ctx, tx, event.entityId)
        return true
      }
      case EventType.CITIZEN_DELETE: {
        await this.opts.citizensRepository.delete(tx, event.entityId, event.createdAt)
        const documents = await this.opts.citizenDocumentsRepository.findAllByCitizenIds(tx, [event.entityId])
        for (const document of documents) {
          await this.opts.citizenDocumentsRepository.delete(tx, document.id, event.createdAt)
        }
        await this.opts.fullTextSearchService.removeFromIndex(ctx, tx, event.entityId)
        return true
      }
    }
    return false
  }
}
